import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useAgent,
  useAgentEmail,
  useAgentEmailMessages,
  useUpdateAgent,
  useUpdateAgentEmail,
} from '@/lib/api/agents';
import {
  useAgentEmailDrafts,
  useApproveOutboundEmail,
  useDiscardOutboundEmail,
  useEmailThread,
  useUpdateOutboundEmail,
} from '@/lib/api/email';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { QueryState } from '@/components/QueryState';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { useWorkspacePath } from '@/lib/workspacePath';
import type { AgentEmailSettings, EmailSendMode, InboundEmailStatus } from '@/lib/api/types';

const PAGE_SIZE = 50;

/** Short human labels for the inbox statuses (spec 0007, FR-5). */
const STATUS_LABELS: Record<InboundEmailStatus, string> = {
  processing: 'Processing',
  triggered: 'Triggered',
  skipped_spam: 'Skipped: spam',
  skipped_auto: 'Skipped: auto-reply',
  skipped_disabled: 'Skipped: disabled',
  skipped_allowlist: 'Skipped: allowlist',
  skipped_rate_limited: 'Skipped: rate limited',
  failed: 'Failed',
};

/** Human labels for outbound statuses in the thread view (spec 0008). */
const OUTBOUND_STATUS_LABELS: Record<string, string> = {
  pending_approval: 'Pending approval',
  sent: 'Sent',
  discarded: 'Discarded',
  failed: 'Failed',
  bounced: 'Bounced',
  complained: 'Complained',
};

/** One line of helper text per send mode (spec 0008, AC-8). */
const SEND_MODE_HELP: Record<EmailSendMode, string> = {
  disabled: 'The agent cannot send email; the model never sees a send tool.',
  approval:
    'The agent writes drafts; a member reviews, edits, and approves or discards each one before anything is sent.',
  automatic:
    'The agent sends immediately, subject to the compose allowlist, send caps, and suppression list.',
};

interface AgentEmailPanelProps {
  agentId: string;
}

/** Address, enabled toggle, and allowlist editor for the agent's email. */
function EmailSettingsSection({
  agentId,
  settings,
}: {
  agentId: string;
  settings: AgentEmailSettings;
}) {
  const update = useUpdateAgentEmail(agentId);
  // null = untouched: render the server's allowlist. Set on first edit; reset
  // back to null after a successful save so the refetched state takes over.
  const [editedRows, setEditedRows] = useState<string[] | null>(null);
  const [copyStatus, setCopyStatus] = useState('');

  const rows = editedRows ?? settings.allowlist;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(settings.address);
      setCopyStatus('Copied');
    } catch {
      setCopyStatus('Copy failed');
    }
    window.setTimeout(() => setCopyStatus(''), 2000);
  }

  function handleSaveAllowlist() {
    const entries = rows.map((r) => r.trim()).filter(Boolean);
    update.mutate({ allowlist: entries }, { onSuccess: () => setEditedRows(null) });
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-1 block text-sm font-medium text-gray-700">Address</p>
        <div className="flex flex-wrap items-center gap-2">
          <code
            className="rounded bg-gray-100 px-2 py-1 text-sm text-gray-900"
            data-testid="agent-email-address"
          >
            {settings.address}
          </code>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void handleCopy()}
            data-testid="copy-email-address-button"
          >
            Copy
          </Button>
          <span
            aria-live="polite"
            className="text-xs text-gray-500"
            data-testid="copy-email-confirmation"
          >
            {copyStatus}
          </span>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Fixed at creation: renaming the agent or workspace never changes it.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={settings.enabled}
          onChange={(e) => update.mutate({ enabled: e.target.checked })}
          disabled={update.isPending}
          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          data-testid="email-enabled-toggle"
        />
        Address enabled (incoming email triggers this agent)
      </label>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">Sender allowlist</label>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setEditedRows([...rows, ''])}
            data-testid="add-allowlist-entry-button"
          >
            Add entry
          </Button>
        </div>
        <p className="text-xs text-gray-500" data-testid="allowlist-hint">
          Entries are exact addresses (person@acme.com) or whole domains (@acme.com). An empty
          list accepts all senders.
        </p>
        {rows.length === 0 && <p className="text-xs text-gray-500">No allowlist entries.</p>}
        {rows.map((entry, index) => (
          <div
            key={index}
            className="flex items-end gap-2"
            data-testid="allowlist-entry-row"
          >
            <div className="flex-1">
              <Input
                label={`Entry ${index + 1}`}
                value={entry}
                onChange={(e) =>
                  setEditedRows(rows.map((r, i) => (i === index ? e.target.value : r)))
                }
                placeholder="person@acme.com or @acme.com"
                data-testid="allowlist-entry-input"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setEditedRows(rows.filter((_, i) => i !== index))}
              data-testid="remove-allowlist-entry-button"
            >
              Remove
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleSaveAllowlist}
          disabled={update.isPending}
          data-testid="save-allowlist-button"
        >
          {update.isPending ? 'Saving...' : 'Save allowlist'}
        </Button>
      </div>

      {update.error && (
        <p
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          data-testid="email-settings-error"
        >
          {(update.error as Error).message}
        </p>
      )}
    </div>
  );
}

/**
 * Send mode selector, platform limits, and the compose allowlist editor
 * (spec 0008, AC-8). Both settings live on the agent row and save through
 * PATCH /agents/{id}.
 */
function SendSettingsSection({ agentId }: { agentId: string }) {
  const agent = useAgent(agentId);
  const update = useUpdateAgent(agentId);
  // Mirrors the select while its PATCH is in flight so the control does not
  // snap back to the server value mid-save.
  const [pendingMode, setPendingMode] = useState<EmailSendMode | null>(null);
  // null = untouched: render the server's allowlist (same pattern as the
  // 0007 sender allowlist above).
  const [editedRows, setEditedRows] = useState<string[] | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);

  const mode = pendingMode ?? agent.data?.email_send_mode ?? 'disabled';
  const rows = editedRows ?? agent.data?.email_compose_allowlist ?? [];

  function handleModeChange(value: EmailSendMode) {
    setClientError(null);
    setPendingMode(value);
    update.mutate({ email_send_mode: value }, { onSettled: () => setPendingMode(null) });
  }

  function handleSaveAllowlist() {
    const entries = rows.map((r) => r.trim()).filter(Boolean);
    const invalid = entries.find((entry) => !entry.includes('@'));
    if (invalid !== undefined) {
      setClientError(
        `Entry "${invalid}" must contain "@": an exact address (person@acme.com) or a domain (@acme.com).`,
      );
      return;
    }
    setClientError(null);
    update.mutate(
      { email_compose_allowlist: entries },
      { onSuccess: () => setEditedRows(null) },
    );
  }

  const errorText = clientError ?? (update.error ? (update.error as Error).message : null);

  return (
    <QueryState
      isLoading={agent.isLoading}
      error={agent.error}
      loadingLabel="Loading send settings"
      skeleton={<SkeletonRows rows={3} />}
    >
      <div className="space-y-4">
        <div className="max-w-xs">
          <Select
            label="Send mode"
            value={mode}
            onChange={(e) => handleModeChange(e.target.value as EmailSendMode)}
            disabled={update.isPending}
            data-testid="email-send-mode-select"
          >
            <option value="disabled">Disabled</option>
            <option value="approval">Approval</option>
            <option value="automatic">Automatic</option>
          </Select>
          <p className="mt-1 text-xs text-gray-500" data-testid="send-mode-help">
            {SEND_MODE_HELP[mode]}
          </p>
        </div>

        <p className="text-xs text-gray-500" data-testid="send-limits">
          Platform send limits (fixed, not configurable): 20 emails per hour, 100 per day, and
          10 replies per thread.
        </p>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="block text-sm font-medium text-gray-700">Compose allowlist</p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setEditedRows([...rows, ''])}
              data-testid="add-compose-allowlist-entry-button"
            >
              Add entry
            </Button>
          </div>
          <p className="text-xs text-gray-500" data-testid="compose-allowlist-hint">
            Recipients the agent may compose new email to: exact addresses (person@acme.com) or
            whole domains (@acme.com). An empty list allows any recipient.
          </p>
          {rows.length === 0 && (
            <p className="text-xs text-gray-500">No entries: any recipient is allowed.</p>
          )}
          {rows.map((entry, index) => (
            <div
              key={index}
              className="flex items-end gap-2"
              data-testid="compose-allowlist-entry-row"
            >
              <div className="flex-1">
                <Input
                  label={`Recipient entry ${index + 1}`}
                  value={entry}
                  onChange={(e) =>
                    setEditedRows(rows.map((r, i) => (i === index ? e.target.value : r)))
                  }
                  placeholder="person@acme.com or @acme.com"
                  data-testid="compose-allowlist-entry-input"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setEditedRows(rows.filter((_, i) => i !== index))}
                data-testid="remove-compose-allowlist-entry-button"
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleSaveAllowlist}
            disabled={update.isPending}
            data-testid="save-compose-allowlist-button"
          >
            {update.isPending ? 'Saving...' : 'Save compose allowlist'}
          </Button>
        </div>

        {errorText && (
          <p
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            data-testid="send-settings-error"
          >
            {errorText}
          </p>
        )}
      </div>
    </QueryState>
  );
}

/** The subset of an outbound record a draft card needs; thread entries and
 * full OutboundEmail rows both satisfy it. */
interface DraftLike {
  id: string;
  subject: string | null;
  body_text: string | null;
  to_address: string | null;
}

/**
 * A pending draft (spec 0008, AC-4/AC-8): subject and body editable inline,
 * with Save (PATCH), Approve (send now), and Discard (confirmed). 409s from
 * caps, kill switch, suppression, or lost races render inline.
 */
function DraftCard({ draft }: { draft: DraftLike }) {
  const [subject, setSubject] = useState(draft.subject ?? '');
  const [body, setBody] = useState(draft.body_text ?? '');
  const [error, setError] = useState<string | null>(null);

  const update = useUpdateOutboundEmail();
  const approve = useApproveOutboundEmail();
  const discard = useDiscardOutboundEmail();
  const busy = update.isPending || approve.isPending || discard.isPending;

  function fail(err: unknown) {
    setError((err as Error).message);
  }

  function handleSave() {
    setError(null);
    update.mutate({ id: draft.id, input: { subject, body_text: body } }, { onError: fail });
  }

  function handleApprove() {
    setError(null);
    approve.mutate(draft.id, { onError: fail });
  }

  function handleDiscard() {
    if (!window.confirm('Discard this draft? It will never be sent.')) return;
    setError(null);
    discard.mutate(draft.id, { onError: fail });
  }

  return (
    <div className="rounded-md border border-brand-200 bg-brand-50 p-3" data-testid="draft-card">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="rounded bg-brand-100 px-1.5 py-0.5 text-xs font-medium text-brand-700">
          Draft pending approval
        </span>
        {draft.to_address && (
          <span className="text-xs text-gray-600" data-testid="draft-to-address">
            To: {draft.to_address}
          </span>
        )}
      </div>

      <div className="space-y-2">
        <Input
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={busy}
          data-testid="draft-subject-input"
        />
        <Textarea
          label="Body"
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={busy}
          data-testid="draft-body-input"
        />
      </div>

      <div aria-live="polite">
        {error && (
          <p
            role="alert"
            className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            data-testid="draft-error"
          >
            {error}
          </p>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleSave}
          disabled={busy}
          data-testid="draft-save-button"
        >
          {update.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleApprove}
          disabled={busy}
          data-testid="draft-approve-button"
        >
          {approve.isPending ? 'Approving...' : 'Approve and send'}
        </Button>
        <Button
          type="button"
          variant="danger"
          size="sm"
          onClick={handleDiscard}
          disabled={busy}
          data-testid="draft-discard-button"
        >
          {discard.isPending ? 'Discarding...' : 'Discard'}
        </Button>
      </div>
    </div>
  );
}

/** All drafts awaiting approval for this agent, reachable without opening
 * each thread (spec 0008, AC-8). */
function PendingDraftsSection({ agentId }: { agentId: string }) {
  const drafts = useAgentEmailDrafts(agentId);
  const items = drafts.data ?? [];

  return (
    <div data-testid="pending-drafts-section">
      <h3 className="mb-3 text-sm font-semibold text-gray-900">Pending drafts</h3>
      <QueryState
        isLoading={drafts.isLoading}
        error={drafts.error}
        isEmpty={items.length === 0}
        emptyMessage="No drafts awaiting approval."
        loadingLabel="Loading drafts"
        skeleton={<SkeletonRows rows={2} />}
      >
        <ul className="space-y-3">
          {items.map((draft) => (
            <li key={draft.id}>
              <DraftCard draft={draft} />
            </li>
          ))}
        </ul>
      </QueryState>
    </div>
  );
}

/**
 * The interleaved conversation of one session (spec 0008, AC-8): inbound
 * mail left-aligned, outbound right-aligned with a status badge, and
 * pending drafts rendered as editable draft cards.
 */
function ThreadView({
  agentId,
  sessionId,
  onClose,
}: {
  agentId: string;
  sessionId: string;
  onClose: () => void;
}) {
  const thread = useEmailThread(agentId, sessionId);
  const entries = thread.data?.entries ?? [];

  return (
    <div className="mt-4 rounded-md border border-gray-200 p-4" data-testid="email-thread-view">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">Thread</h4>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          data-testid="close-thread-button"
        >
          Close
        </Button>
      </div>
      <QueryState
        isLoading={thread.isLoading}
        error={thread.error}
        isEmpty={entries.length === 0}
        emptyMessage="No messages in this thread yet."
        loadingLabel="Loading thread"
        skeleton={<SkeletonRows rows={3} />}
      >
        <ul className="space-y-3">
          {entries.map((entry) => (
            <li key={`${entry.kind}-${entry.id}`} data-testid="thread-entry" data-kind={entry.kind}>
              {entry.kind === 'outbound' && entry.status === 'pending_approval' ? (
                <div className="ml-8">
                  <DraftCard
                    draft={{
                      id: entry.id,
                      subject: entry.subject,
                      body_text: entry.body_text ?? null,
                      to_address: entry.to_address ?? null,
                    }}
                  />
                </div>
              ) : (
                <div
                  className={
                    entry.kind === 'inbound'
                      ? 'mr-8 rounded-md border border-gray-200 bg-gray-50 p-3'
                      : 'ml-8 rounded-md border border-brand-100 bg-brand-50 p-3'
                  }
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                    <span>
                      <span
                        className={
                          entry.kind === 'inbound'
                            ? 'rounded bg-gray-200 px-1.5 py-0.5 text-[11px] font-medium text-gray-700'
                            : 'rounded bg-brand-100 px-1.5 py-0.5 text-[11px] font-medium text-brand-700'
                        }
                      >
                        {entry.kind === 'inbound'
                          ? 'Received'
                          : OUTBOUND_STATUS_LABELS[entry.status] ?? entry.status}
                      </span>{' '}
                      {entry.kind === 'inbound'
                        ? entry.from_address && `from ${entry.from_address}`
                        : entry.to_address && `to ${entry.to_address}`}
                    </span>
                    <span>{new Date(entry.sent_at ?? entry.occurred_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {entry.subject ?? '(no subject)'}
                  </p>
                  {entry.body_text && (
                    <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">
                      {entry.body_text}
                    </p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </QueryState>
    </div>
  );
}

/**
 * Email panel for an agent (spec 0007, AC-8): the agent's inbound address
 * with a copy control, the enabled toggle, the sender allowlist editor, and
 * the received-message list where triggered rows link to the conversation
 * transcript (the existing chat view, loaded via ?session=).
 * Spec 0008 (AC-8) adds the send-mode selector with the compose allowlist,
 * the pending-drafts list, and the interleaved per-session thread view.
 */
export function AgentEmailPanel({ agentId }: AgentEmailPanelProps) {
  const ws = useWorkspacePath();
  const settings = useAgentEmail(agentId);
  const [offset, setOffset] = useState(0);
  const [openThreadSessionId, setOpenThreadSessionId] = useState<string | null>(null);
  const messages = useAgentEmailMessages(agentId, { limit: PAGE_SIZE, offset });

  const items = messages.data?.items ?? [];
  const total = messages.data?.total ?? 0;

  return (
    <Card title="Email" description="This agent's email address, send settings, and inbox.">
      <QueryState
        isLoading={settings.isLoading}
        error={settings.error}
        loadingLabel="Loading email settings"
        skeleton={<SkeletonRows rows={3} />}
      >
        {settings.data && <EmailSettingsSection agentId={agentId} settings={settings.data} />}
      </QueryState>

      <div className="mt-6 border-t border-gray-100 pt-4">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Sending</h3>
        <SendSettingsSection agentId={agentId} />
      </div>

      <div className="mt-6 border-t border-gray-100 pt-4">
        <PendingDraftsSection agentId={agentId} />
      </div>

      <div className="mt-6 border-t border-gray-100 pt-4">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Received messages</h3>
        <QueryState
          isLoading={messages.isLoading}
          error={messages.error}
          isEmpty={items.length === 0}
          emptyMessage="No emails received yet."
          loadingLabel="Loading received messages"
          skeleton={<SkeletonRows rows={3} />}
        >
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase text-gray-500">
                <th className="py-2">From</th>
                <th className="py-2">Subject</th>
                <th className="py-2">Received</th>
                <th className="py-2">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((message) => (
                <tr key={message.id} data-testid="email-message-row">
                  <td className="py-2">{message.from_address}</td>
                  <td className="py-2">{message.subject ?? '(no subject)'}</td>
                  <td className="py-2">{new Date(message.received_at).toLocaleString()}</td>
                  <td className="py-2">
                    <span data-testid="email-message-status">
                      {STATUS_LABELS[message.status] ?? message.status}
                    </span>
                    {message.has_outbound && (
                      <span
                        className="ml-2 rounded bg-brand-100 px-1.5 py-0.5 text-[11px] font-medium text-brand-700"
                        data-testid="email-message-replied-badge"
                      >
                        Replied
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <span className="inline-flex items-center gap-3">
                      {message.session_id && (
                        <button
                          type="button"
                          onClick={() => setOpenThreadSessionId(message.session_id)}
                          className="text-brand-600 hover:underline"
                          data-testid="email-view-thread-button"
                        >
                          Thread
                        </button>
                      )}
                      {message.status === 'triggered' && message.session_id && (
                        <Link
                          to={`${ws('agents', agentId, 'chat')}?session=${message.session_id}`}
                          className="text-brand-600 hover:underline"
                          data-testid="email-view-conversation-link"
                        >
                          View conversation
                        </Link>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </QueryState>

        {total > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              data-testid="email-prev-page-button"
            >
              Previous
            </Button>
            <span>
              {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              data-testid="email-next-page-button"
            >
              Next
            </Button>
          </div>
        )}

        {openThreadSessionId && (
          <ThreadView
            agentId={agentId}
            sessionId={openThreadSessionId}
            onClose={() => setOpenThreadSessionId(null)}
          />
        )}
      </div>
    </Card>
  );
}
